//#region src/audit-text-bounds.ts
/**
* ⛔ MOVED from `cloud/dp/core-api/src/audit/audit-text-bounds.ts` unchanged. It has a
* THIRD consumer now: the control-plane publish gate that refuses a manifest declaring a
* `details` key Postgres cannot store. A control-plane service cannot import a data-plane
* module, and §7's one-definition rule does not bend for a third consumer.
*
* Storability and size bounds shared by the audit emit path and the audit ingest
* route.
*
* ⛔ A THIRD MODULE, AND NOT AN EXPORT FROM EITHER EXISTING FILE, BECAUSE EITHER
* WOULD BE A CYCLE. `audit.service.ts` already imports `{ isIpv4, isIpv6 }` from
* `./audit-internal.schema`, so putting the storability predicate on the service
* and importing it from the schema closes a loop, and this repository forbids the
* dynamic import that would paper over it. A module both files import is the only
* shape with one definition and no cycle.
*
* This module depends on nothing, deliberately: a module both the schema and the
* service import must not pull either one's dependencies back in.
*/
/**
* What Postgres refuses in a `text` or `jsonb` value: U+0000, and any surrogate
* that is not part of a well-formed pair.
*
* ⛔ ONE DEFINITION, TWO CONSUMERS, AND THAT IS THE WHOLE REASON THIS MODULE
* EXISTS. The emit path SCRUBS against this pattern and the ingest route REJECTS
* against it. Two separate regexes would mean the route accepts what the writer
* drops, or the reverse — the identical argument the `isIpv4` / `isIpv6` export
* already makes for addresses.
*
* ⛔ THE LOOKAHEAD / LOOKBEHIND PAIR IS WHAT MAKES THIS A BOUND AND NOT A
* MANGLER: a WELL-FORMED surrogate pair must pass, so an emoji in a display name
* is not treated as unstorable.
*
* ⚠️ THE `g` FLAG IS LOAD-BEARING IN BOTH DIRECTIONS. `scrubText` on the emit
* path needs it so `String.prototype.replace` reaches every occurrence, and
* {@link isPostgresStorable} needs its `lastIndex` reset BECAUSE of it.
*/
const POSTGRES_UNSTORABLE = /\u0000|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;
/**
* Whether `value` can be stored as-is.
*
* ⛔ RESETS `lastIndex`. {@link POSTGRES_UNSTORABLE} carries the `g` flag because
* the scrubber needs it for `replace`, and `RegExp.prototype.test` on a `g` regex
* ADVANCES `lastIndex`, so consecutive calls against different strings give wrong
* answers. A predicate that is correct on its first call and wrong on its second
* is the worst available failure mode: the first test written passes.
*
* ⚠️ MEASURED without the reset: three consecutive calls on unstorable strings
* returned `false`, then `true`, then `false`. The predicate ALTERNATES, so it
* reports roughly every other unstorable payload as storable. That is why the
* reset is on ENTRY rather than on exit — an exit-only reset leaves the regex
* dirty if `test` throws, and an entry reset is correct however the previous
* call ended.
*/
function isPostgresStorable(value) {
	POSTGRES_UNSTORABLE.lastIndex = 0;
	return !POSTGRES_UNSTORABLE.test(value);
}
/** The single rejection message all three storability refines share. */
const UNSTORABLE_MESSAGE = "value contains characters PostgreSQL cannot store (U+0000 or an unpaired surrogate)";
/** Serialized-size ceiling for a `details` payload. Shared with the emit path. */
const AUDIT_DETAILS_MAX_BYTES = 65536;
/**
* Nesting-depth ceiling for a `details` payload: the greatest permitted depth of
* any value inside it, counted as the number of enclosing containers.
*
* ⛔⛔ THIS IS A DIRECT BOUND ON DEPTH BECAUSE A BYTE CAP CANNOT BOUND DEPTH.
* C-028 corrects C-016. C-016 ruled no depth cap was needed and that ruling
* rested on a measurement of the NODE stack — a recursive walk dies at 10,000
* levels while `JSON.parse` survives 100,000 — which the iterative walk in
* `audit-internal.schema.ts` answers. It never analysed the POSTGRES stack,
* which is a different mechanism with a different limit: `jsonb` input parsing
* is recursive under `check_stack_depth()`, and past its limit the database
* answers SQLSTATE `54001`. This service registers no exception filter, so that
* is a bare 500 with no row — the A11 outcome C-016 exists to remove.
*
* ⛔ THE ATTEMPT TO CLOSE IT TRANSITIVELY FAILED, AND THE REASON GENERALISES.
* {@link AUDIT_DETAILS_MAX_BYTES} was pinned against an assumed 6 bytes per
* nesting level, which is the cost of OBJECT nesting (`{"a":` plus `}`).
* `details` is `z.record(z.string(), z.unknown())`, so a value may be an ARRAY,
* and array nesting costs 2 (`[` plus `]`). MEASURED: the 65,536-byte cap admits
* depth about 10,922 in objects and about 32,768 in arrays, and through the real
* pg driver into a `jsonb` column a 32 KB array-nested body — half the byte cap —
* answered `54001`, with a depth-10 control inserting cleanly.
* ⇒ BYTES PER LEVEL IS A PROPERTY OF THE SHAPE, NOT OF THE PAYLOAD, so no byte
* cap can bound depth for a class that admits more than one shape. Bound depth
* directly instead.
*
* ⭐ WHY 64. Postgres rejected ARRAY nesting at depth 15,000 on the 2MB default
* stack, and a 1MB stack roughly halves that. 64 is about two orders of
* magnitude below the tightest measured failure, and about an order of magnitude
* above any plausible legitimate audit payload — this is event metadata, and
* realistically under 10 deep. Above all it is SHAPE-INDEPENDENT: it bounds
* depth itself, so it holds for objects, for arrays and for any mix of the two,
* which is precisely the property the byte cap does not have.
*/
const AUDIT_DETAILS_MAX_DEPTH = 64;
/** Character ceiling for one scrubbed text value on the emit path. */
const AUDIT_TEXT_MAX_CHARS = 4096;
//#endregion
export { AUDIT_DETAILS_MAX_BYTES, AUDIT_DETAILS_MAX_DEPTH, AUDIT_TEXT_MAX_CHARS, POSTGRES_UNSTORABLE, UNSTORABLE_MESSAGE, isPostgresStorable };

//# sourceMappingURL=audit-text-bounds.js.map